import {
  CS_DASHBOARD_FETCH_REQUEST,
  CS_DASHBOARD_FETCH_SUCCESS,
  CS_DASHBOARD_FETCH_FAILURE,
  CS_COMPANIES_FETCH_REQUEST,
  CS_COMPANIES_FETCH_SUCCESS,
  CS_COMPANIES_FETCH_FAILURE,
  CS_COMPANIES_SET_SEARCH,
  CS_COMPANIES_SET_HEALTH_FILTER,
  CS_COMPANIES_SET_ACTIVITY_FILTER,
  CS_COMPANIES_SET_ENGAGEMENT_FILTER,
  CS_COMPANIES_SET_HAS_CONTRACT_FILTER,
  CS_COMPANIES_SET_PAGE,
  CS_COMPANIES_SET_LIMIT,
  CS_COMPANY_DETAIL_FETCH_REQUEST,
  CS_COMPANY_DETAIL_FETCH_SUCCESS,
  CS_COMPANY_DETAIL_FETCH_FAILURE,
  CS_COMPANY_DETAIL_CLEAR,
  CS_CONTRACTS_FETCH_REQUEST,
  CS_CONTRACTS_FETCH_SUCCESS,
  CS_CONTRACTS_FETCH_FAILURE,
  CS_CONTRACTS_SET_SEARCH,
  CS_CONTRACTS_SET_STATUS_FILTER,
  CS_CONTRACT_DETAIL_FETCH_REQUEST,
  CS_CONTRACT_DETAIL_FETCH_SUCCESS,
  CS_CONTRACT_DETAIL_FETCH_FAILURE,
  CS_CONTRACT_DETAIL_CLEAR,
  CS_TALENT_FETCH_REQUEST,
  CS_TALENT_FETCH_SUCCESS,
  CS_TALENT_FETCH_FAILURE,
  CS_TALENT_SET_SEARCH,
  CS_ISSUES_FETCH_REQUEST,
  CS_ISSUES_FETCH_SUCCESS,
  CS_ISSUES_FETCH_FAILURE,
  CS_ISSUES_SET_FILTERS,
  CS_ISSUE_DETAIL_FETCH_REQUEST,
  CS_ISSUE_DETAIL_FETCH_SUCCESS,
  CS_ISSUE_DETAIL_FETCH_FAILURE,
  CS_ISSUE_DETAIL_CLEAR,
  CS_REPORTS_FETCH_REQUEST,
  CS_REPORTS_FETCH_SUCCESS,
  CS_REPORTS_FETCH_FAILURE,
  CS_EMAIL_TEMPLATES_FETCH_REQUEST,
  CS_EMAIL_TEMPLATES_FETCH_SUCCESS,
  CS_EMAIL_TEMPLATES_FETCH_FAILURE,
  CS_SCHEDULED_EMAILS_FETCH_REQUEST,
  CS_SCHEDULED_EMAILS_FETCH_SUCCESS,
  CS_SCHEDULED_EMAILS_FETCH_FAILURE,
} from '../actions/actionTypes';
import type { CsAction } from '../actions/csActions';

interface AsyncList<T = any> {
  data: T[];
  loading: boolean;
  error: string | null;
}

interface AsyncDetail<T = any> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

export interface CsState {
  dashboard: AsyncDetail;
  companies: AsyncList & {
    search: string;
    healthFilter: string;
    activityFilter: string;
    /** `''` | `'full_time'` | `'part_time'` → `engagement=` on GET /api/cs/companies. */
    engagementFilter: string;
    /** → `hasContract=true` on GET /api/cs/companies. */
    hasContractFilter: boolean;
    total: number;
    page: number;
    limit: number;
  };
  companyDetail: AsyncDetail;
  contracts: AsyncList & { search: string; statusFilter: string };
  contractDetail: AsyncDetail;
  talent: AsyncList & { search: string };
  issues: AsyncList & { filters: Record<string, string> };
  issueDetail: AsyncDetail;
  reports: AsyncDetail;
  emailTemplates: AsyncList;
  scheduledEmails: AsyncList;
  // Upsells live outside Redux (local state + apiFetch on the upsell pages).
}

const initialState: CsState = {
  dashboard: { data: null, loading: false, error: null },
  companies: { data: [], loading: false, error: null, search: '', healthFilter: '', activityFilter: '', engagementFilter: '', hasContractFilter: false, total: 0, page: 1, limit: 25 },
  companyDetail: { data: null, loading: false, error: null },
  contracts: { data: [], loading: false, error: null, search: '', statusFilter: '' },
  contractDetail: { data: null, loading: false, error: null },
  talent: { data: [], loading: false, error: null, search: '' },
  issues: { data: [], loading: false, error: null, filters: {} },
  issueDetail: { data: null, loading: false, error: null },
  reports: { data: null, loading: false, error: null },
  emailTemplates: { data: [], loading: false, error: null },
  scheduledEmails: { data: [], loading: false, error: null },
};

export default function csReducer(state = initialState, action: CsAction): CsState {
  switch (action.type) {
    // ─── Dashboard ────────────────────────────────────────────────
    case CS_DASHBOARD_FETCH_REQUEST:
      return { ...state, dashboard: { ...state.dashboard, loading: true, error: null } };
    case CS_DASHBOARD_FETCH_SUCCESS:
      return { ...state, dashboard: { data: action.payload, loading: false, error: null } };
    case CS_DASHBOARD_FETCH_FAILURE:
      return { ...state, dashboard: { ...state.dashboard, loading: false, error: action.payload } };

    // ─── Companies ────────────────────────────────────────────────
    case CS_COMPANIES_FETCH_REQUEST:
      return { ...state, companies: { ...state.companies, loading: true, error: null } };
    case CS_COMPANIES_FETCH_SUCCESS:
      return { ...state, companies: { ...state.companies, data: action.payload.data, total: action.payload.total, page: action.payload.page, limit: action.payload.limit, loading: false, error: null } };
    case CS_COMPANIES_FETCH_FAILURE:
      return { ...state, companies: { ...state.companies, loading: false, error: action.payload } };
    case CS_COMPANIES_SET_SEARCH:
      return { ...state, companies: { ...state.companies, search: action.payload, page: 1 } };
    case CS_COMPANIES_SET_HEALTH_FILTER:
      return { ...state, companies: { ...state.companies, healthFilter: action.payload, page: 1 } };
    case CS_COMPANIES_SET_ACTIVITY_FILTER:
      return { ...state, companies: { ...state.companies, activityFilter: action.payload, page: 1 } };
    case CS_COMPANIES_SET_ENGAGEMENT_FILTER:
      return { ...state, companies: { ...state.companies, engagementFilter: action.payload, page: 1 } };
    case CS_COMPANIES_SET_HAS_CONTRACT_FILTER:
      return { ...state, companies: { ...state.companies, hasContractFilter: action.payload, page: 1 } };
    case CS_COMPANIES_SET_PAGE:
      return { ...state, companies: { ...state.companies, page: action.payload } };
    case CS_COMPANIES_SET_LIMIT:
      return { ...state, companies: { ...state.companies, limit: action.payload, page: 1 } };

    // ─── Company Detail ───────────────────────────────────────────
    case CS_COMPANY_DETAIL_FETCH_REQUEST:
      return { ...state, companyDetail: { ...state.companyDetail, loading: true, error: null } };
    case CS_COMPANY_DETAIL_FETCH_SUCCESS:
      return { ...state, companyDetail: { data: action.payload, loading: false, error: null } };
    case CS_COMPANY_DETAIL_FETCH_FAILURE:
      return { ...state, companyDetail: { ...state.companyDetail, loading: false, error: action.payload } };
    case CS_COMPANY_DETAIL_CLEAR:
      return { ...state, companyDetail: { data: null, loading: false, error: null } };

    // ─── Contracts ────────────────────────────────────────────────
    case CS_CONTRACTS_FETCH_REQUEST:
      return { ...state, contracts: { ...state.contracts, loading: true, error: null } };
    case CS_CONTRACTS_FETCH_SUCCESS:
      return { ...state, contracts: { ...state.contracts, data: action.payload, loading: false, error: null } };
    case CS_CONTRACTS_FETCH_FAILURE:
      return { ...state, contracts: { ...state.contracts, loading: false, error: action.payload } };
    case CS_CONTRACTS_SET_SEARCH:
      return { ...state, contracts: { ...state.contracts, search: action.payload } };
    case CS_CONTRACTS_SET_STATUS_FILTER:
      return { ...state, contracts: { ...state.contracts, statusFilter: action.payload } };

    // ─── Contract Detail ──────────────────────────────────────────
    case CS_CONTRACT_DETAIL_FETCH_REQUEST:
      return { ...state, contractDetail: { ...state.contractDetail, loading: true, error: null } };
    case CS_CONTRACT_DETAIL_FETCH_SUCCESS:
      return { ...state, contractDetail: { data: action.payload, loading: false, error: null } };
    case CS_CONTRACT_DETAIL_FETCH_FAILURE:
      return { ...state, contractDetail: { ...state.contractDetail, loading: false, error: action.payload } };
    case CS_CONTRACT_DETAIL_CLEAR:
      return { ...state, contractDetail: { data: null, loading: false, error: null } };

    // ─── Talent ───────────────────────────────────────────────────
    case CS_TALENT_FETCH_REQUEST:
      return { ...state, talent: { ...state.talent, loading: true, error: null } };
    case CS_TALENT_FETCH_SUCCESS:
      return { ...state, talent: { ...state.talent, data: action.payload, loading: false, error: null } };
    case CS_TALENT_FETCH_FAILURE:
      return { ...state, talent: { ...state.talent, loading: false, error: action.payload } };
    case CS_TALENT_SET_SEARCH:
      return { ...state, talent: { ...state.talent, search: action.payload } };

    // ─── Issues ───────────────────────────────────────────────────
    case CS_ISSUES_FETCH_REQUEST:
      return { ...state, issues: { ...state.issues, loading: true, error: null } };
    case CS_ISSUES_FETCH_SUCCESS:
      return { ...state, issues: { ...state.issues, data: action.payload, loading: false, error: null } };
    case CS_ISSUES_FETCH_FAILURE:
      return { ...state, issues: { ...state.issues, loading: false, error: action.payload } };
    case CS_ISSUES_SET_FILTERS:
      return { ...state, issues: { ...state.issues, filters: { ...state.issues.filters, ...action.payload } } };

    // ─── Issue Detail ─────────────────────────────────────────────
    case CS_ISSUE_DETAIL_FETCH_REQUEST:
      return { ...state, issueDetail: { ...state.issueDetail, loading: true, error: null } };
    case CS_ISSUE_DETAIL_FETCH_SUCCESS:
      return { ...state, issueDetail: { data: action.payload, loading: false, error: null } };
    case CS_ISSUE_DETAIL_FETCH_FAILURE:
      return { ...state, issueDetail: { ...state.issueDetail, loading: false, error: action.payload } };
    case CS_ISSUE_DETAIL_CLEAR:
      return { ...state, issueDetail: { data: null, loading: false, error: null } };

    // ─── Reports ──────────────────────────────────────────────────
    case CS_REPORTS_FETCH_REQUEST:
      return { ...state, reports: { ...state.reports, loading: true, error: null } };
    case CS_REPORTS_FETCH_SUCCESS:
      return { ...state, reports: { data: action.payload, loading: false, error: null } };
    case CS_REPORTS_FETCH_FAILURE:
      return { ...state, reports: { ...state.reports, loading: false, error: action.payload } };

    // ─── Email Templates ──────────────────────────────────────────
    case CS_EMAIL_TEMPLATES_FETCH_REQUEST:
      return { ...state, emailTemplates: { ...state.emailTemplates, loading: true, error: null } };
    case CS_EMAIL_TEMPLATES_FETCH_SUCCESS:
      return { ...state, emailTemplates: { data: action.payload, loading: false, error: null } };
    case CS_EMAIL_TEMPLATES_FETCH_FAILURE:
      return { ...state, emailTemplates: { ...state.emailTemplates, loading: false, error: action.payload } };

    // ─── Scheduled Emails ─────────────────────────────────────────
    case CS_SCHEDULED_EMAILS_FETCH_REQUEST:
      return { ...state, scheduledEmails: { ...state.scheduledEmails, loading: true, error: null } };
    case CS_SCHEDULED_EMAILS_FETCH_SUCCESS:
      return { ...state, scheduledEmails: { data: action.payload, loading: false, error: null } };
    case CS_SCHEDULED_EMAILS_FETCH_FAILURE:
      return { ...state, scheduledEmails: { ...state.scheduledEmails, loading: false, error: action.payload } };

    default:
      return state;
  }
}
